import axios from 'axios'
import { ref } from 'vue'
import { useToast } from 'vue-toast-notification'

export function usePlans({
    onCreated = () => {},
    onUpdated = () => {},
    onDeleted = () => {},
    onFetched = () => {},
    onError = () => {},
}) {
    const $toast = useToast()

    const loading = ref(false)
    const creating = ref(false)
    const updating = ref(false)
    const deleting = ref(false)
    const plan = ref(null)
    const plans = ref([])
    const modules = ref([])
    const allCategories = ref([])
    const errors = ref(null)

    const fetchPlans = async () => {
        loading.value = true
        try {
            const response = await axios.get('/api/admin/plans')
            plans.value = response.data.data
            return plans.value
        } catch (error) {
            errors.value = error.response?.data?.errors
            onError(error)
            return []
        } finally {
            loading.value = false
        }
    }

    const fetchPlan = async (planId) => {
        loading.value = true
        try {
            const response = await axios.get(`/api/admin/plans/${planId}`)
            plan.value = response.data.data
            onFetched(plan.value)
            return plan.value
        } catch (error) {
            errors.value = error.response?.data?.errors
            onError(error)
            return null
        } finally {
            loading.value = false
        }
    }

    const fetchModules = async ({ paidOnly = false } = {}) => {
        try {
            const params = paidOnly ? { paid_only: true } : {}
            const response = await axios.get('/api/admin/modules', { params })
            modules.value = response.data.data
            return modules.value
        } catch (error) {
            errors.value = error.response?.data?.errors
            onError(error)
            return []
        }
    }

    const fetchAllCategories = async () => {
        try {
            const typeLabels = {
                'App\\Models\\HelpArticle': 'Article d\'aide',
                'App\\Models\\Ticket': 'Ticket',
                'App\\Models\\News': 'Actualité',
            }

            // Define sort order for types
            const typeOrder = Object.keys(typeLabels)

            const response = await axios.get('/api/categories', {
                params: {
                    sortingField: 'name',
                    sortingOrder: 'asc',
                    all: true,
                },
            })

            const categories = response.data.data || []

            allCategories.value = categories
                .map(cat => {
                    const catData = cat.data || cat
                    const typeLabel = typeLabels[catData.categorizable_type] || catData.categorizable_type
                    return {
                        id: `${catData.id}_${catData.categorizable_type}`,
                        categoryId: catData.id,
                        categoryType: catData.categorizable_type,
                        name: `${typeLabel} - ${catData.name}`,
                        rawName: catData.name,
                    }
                })
                // Sort by type first, then by name
                .sort((a, b) => {
                    const typeIndexA = typeOrder.indexOf(a.categoryType)
                    const typeIndexB = typeOrder.indexOf(b.categoryType)
                    if (typeIndexA !== typeIndexB) {
                        return typeIndexA - typeIndexB
                    }
                    return a.rawName.localeCompare(b.rawName, 'fr')
                })

            return allCategories.value
        } catch (error) {
            errors.value = error.response?.data?.errors
            onError(error)
            return allCategories.value
        }
    }

    const createPlan = async (form) => {
        creating.value = true
        errors.value = null
        try {
            const response = await axios.post('/api/admin/plans', form)
            $toast.success('Plan créé avec succès', {
                position: 'top',
                duration: 5000,
                dismissible: true,
                pauseOnHover: true,
            })
            onCreated(response.data.data)
            return response.data.data
        } catch (error) {
            errors.value = error.response?.data?.errors
            if (error.response?.status !== 422) {
                $toast.error('Erreur lors de la création du plan', {
                    position: 'top',
                    duration: 5000,
                    dismissible: true,
                    pauseOnHover: true,
                })
            }
            onError(error)
            return null
        } finally {
            creating.value = false
        }
    }

    const updatePlan = async (planId, form) => {
        updating.value = true
        errors.value = null
        try {
            const response = await axios.put(`/api/admin/plans/${planId}`, form)
            $toast.success('Plan mis à jour avec succès', {
                position: 'top',
                duration: 5000,
                dismissible: true,
                pauseOnHover: true,
            })
            onUpdated(response.data.data)
            return response.data.data
        } catch (error) {
            errors.value = error.response?.data?.errors
            if (error.response?.status !== 422) {
                $toast.error('Erreur lors de la mise à jour du plan', {
                    position: 'top',
                    duration: 5000,
                    dismissible: true,
                    pauseOnHover: true,
                })
            }
            onError(error)
            return null
        } finally {
            updating.value = false
        }
    }

    const deletePlan = async (planId) => {
        deleting.value = true
        try {
            await axios.delete(`/api/admin/plans/${planId}`)
            $toast.success('Plan supprimé avec succès', {
                position: 'top',
                duration: 5000,
                dismissible: true,
                pauseOnHover: true,
            })
            onDeleted(planId)
            return true
        } catch (error) {
            errors.value = error.response?.data?.errors
            $toast.error('Erreur lors de la suppression du plan', {
                position: 'top',
                duration: 5000,
                dismissible: true,
                pauseOnHover: true,
            })
            onError(error)
            return false
        } finally {
            deleting.value = false
        }
    }

    const syncToStripe = async (planId) => {
        loading.value = true
        try {
            const response = await axios.post(`/api/admin/plans/${planId}/sync`)
            $toast.success('Plan synchronisé avec Stripe', {
                position: 'top',
                duration: 5000,
                dismissible: true,
                pauseOnHover: true,
            })
            return response.data.data
        } catch (error) {
            errors.value = error.response?.data?.errors
            $toast.error('Erreur lors de la synchronisation avec Stripe', {
                position: 'top',
                duration: 5000,
                dismissible: true,
                pauseOnHover: true,
            })
            onError(error)
            return null
        } finally {
            loading.value = false
        }
    }

    return {
        loading,
        creating,
        updating,
        deleting,
        plan,
        plans,
        modules,
        allCategories,
        errors,
        fetchPlans,
        fetchPlan,
        fetchModules,
        fetchAllCategories,
        createPlan,
        updatePlan,
        deletePlan,
        syncToStripe,
    }
}
